import { Form, Steps } from 'antd';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  AssetPackInterface,
  GroupedPacks,
  PackTemplate,
} from '../../../interfaces/AssetPacks';
import { AssetsService } from '../../../services/assets/Assets';
import { GroupsService } from '../../../services/groups/Groups';
import { UsersService } from '../../../services/users/Users';
import { clearAside } from '../../../state/component/AsideSlice';
import { RootState } from '../../../state/store';
import { addToast } from '../../../state/toast/ToastSlice';
import { MapUserForOnboarding } from '../../../utils/mappers/UserMapper';
import Cta from '../../buttons/Cta';
import LoadingSpinner from '../../buttons/LoadingSpinner';
import SelectAssetPacksForm from '../assets/SelectAssetPacksForm';
import HardwareOnboardingComfirmation from './HardwareOnboardingComfirmation';
import UserOnboardingForm from './UserOnboardingForm';

const IctOnboardingForm = () => {
  const [form] = Form.useForm();
  const [current, setCurrent] = useState<number>(0);
  const [userGroups, setUserGroups] = useState([]);
  const [userGroup, setUserGroup] = useState<string>();
  const [overviewData, setOverviewData] = useState([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [unassignedAssets, setUnassignedAssets] = useState([]);
  const dispatch = useDispatch();

  const assetService = new AssetsService();
  const groupService = new GroupsService();
  const userService = new UsersService();
  const companyData = useSelector(
    (state: RootState) => state.companies.selectedCompany
  );
  const companyId = companyData?.id;
  const companyName = companyData?.companyName;

  const [packs, setPacks] = useState<GroupedPacks>();
  const [selectedAssetPacks, setSelectedAssetPacks] = useState<string[]>([]);

  if (!companyId) {
    return 'Error';
  }

  const handlePackSelection = (selectedIds: string[], packType: string) => {
    if (packType === 'old') {
      form.setFieldsValue({
        unassignedHardware: selectedIds,
      });
    } else {
      form.setFieldsValue({
        assetPackSelection: selectedIds,
      });
    }
  };

  const next = async () => {
    if (current === 1) {
      const data = transformFormDataForConfirmation(form.getFieldsValue(true));
      setOverviewData(data);
    }
    const values = await form.validateFields();
    setCurrent(current + 1);
  };

  const prev = () => {
    setCurrent(current - 1);
  };
  const fetchUserGroups = async () => {
    const groups = await groupService.getGroups(companyId, '');
    const result = groups.map((group) => ({
      label: group.bedGr_naam,
      value: String(group.bedGr_id),
    }));
    setUserGroups(result);
  };

  const handleGroupSelect = (groupId: string) => {
    if (groupId === userGroup) return;
    setUserGroup(groupId);

    form.setFieldsValue({
      assetPackSelection: [],
    });
  };

  const safeToArray = (input: any) => {
    if (!input) return [];
    return Array.isArray(input) ? input : Object.values(input);
  };

  const transformFormDataForConfirmation = (rawValues: any): any => {
    const groupLookup = userGroups.find((g) => {
      console.log('g', g);
      return String(g.value) === rawValues.geb_groep_id;
    });
    const groupName = groupLookup ? groupLookup.label : 'Groep onbekend';

    const safeSelectedPacks = safeToArray(rawValues.assetPackSelection);
    const safeUnassignedHardware = safeToArray(rawValues.unassignedHardware);

    const selectedPacksDisplay: AssetPackInterface[] = safeSelectedPacks.map(
      (selectedPack: AssetPackInterface) => {
        let packLookup: PackTemplate | undefined;
        for (const typeKey of Object.keys(packs)) {
          const foundPack = packs[typeKey].find(
            (p: PackTemplate) => p.PackTemplateID === selectedPack.id
          );

          if (foundPack) {
            packLookup = foundPack;
            break;
          }
        }

        return {
          ...packLookup,
          id: selectedPack.id,
          name: selectedPack.name,
          type: packLookup?.PackType || 'ONBEKEND',
        };
      }
    );

    const unassignedHardwareDisplay: AssetPackInterface[] =
      safeUnassignedHardware.map((asset: AssetPackInterface) => ({
        id: asset.id,
        name: asset.name,
      }));

    const apiSelectedPacksIds: string[] = safeSelectedPacks.map(
      (pack: AssetPackInterface) => String(pack.id)
    );

    const apiSelectedAssetsIds: number[] = safeUnassignedHardware.map(
      (asset: AssetPackInterface) => Number(asset.id)
    );

    return {
      groupName: groupName,
      name: rawValues.geb_voornaam,
      surname: rawValues.geb_naam,
      email: rawValues.email,
      selectedPacks: selectedPacksDisplay,
      unassignedHardware: unassignedHardwareDisplay,

      onboarding: {
        onboarding: {
          selectedPacks: apiSelectedPacksIds,
          selectedAssets: apiSelectedAssetsIds,
        },
      },
      cleanInstall: rawValues.cleanInstall,
      contact: rawValues.contact,
      agree: rawValues.agree,
      companyName: companyName,
    };
  };

  const fetchAssetPacks = async () => {
    const result = await assetService.getAssetPacksForGroup(
      companyId.toString(),
      userGroup ?? '0'
    );

    const grouped = result.packs.reduce(
      (acc: GroupedPacks, pack: PackTemplate) => {
        const type = pack.PackType;
        acc[type] = acc[type] || [];
        acc[type].push(pack);
        return acc;
      },
      {} as GroupedPacks
    );

    setPacks(grouped);
  };

  const fetchUnassignedHardware = async () => {
    const result = await assetService.getAvailableAssets(companyId.toString());
    const filter = ['SRV'];
    const usableAssets = result.filter(
      (item) =>
        item.ordPro_naam &&
        !filter.some((filterItem) => item.ordPro_naam.includes(filterItem))
    );

    setUnassignedAssets(usableAssets);
  };

  const onChangeSteps = (value: number) => {
    setCurrent(value);
  };

  const handleSubmit = async () => {
    setLoading(true);
    const values = form.getFieldsValue(true);
    if (!values.agree) {
      dispatch(
        addToast({
          description: 'Gelieve akkoord te gaan',
          position: 'bottomRight',
          style: 'error',
        })
      );
      return;
    }

    const onboardingData = MapUserForOnboarding(values);
    try {
      await userService.createOnboarding(companyId, {
        companyName: companyName,
        ...onboardingData,
      });
    } catch (error: any) {
      dispatch(
        addToast({
          description: 'Er is iets fout gegaan bij de onboarding',
          position: 'bottomRight',
          style: 'error',
        })
      );
      setLoading(false);
    }

    setLoading(false);
    dispatch(clearAside('Onboarding'));
    dispatch(
      addToast({
        description: 'Gebruiker onboarding is succesvol',
        position: 'bottomRight',
        style: 'success',
      })
    );
  };

  const steps = [
    {
      title: 'Gebruikersgegevens',
      content: userGroups ? (
        <UserOnboardingForm
          groups={userGroups}
          onGroupSelect={handleGroupSelect}
        />
      ) : (
        <LoadingSpinner />
      ),
    },
    {
      title: 'Packs',
      content: (
        <SelectAssetPacksForm
          packs={packs}
          initialSelection={form.getFieldValue('assetPackSelection') || []}
          initialUnassignedAssets={
            form.getFieldValue('unassignedHardware') || []
          }
          onclick={handlePackSelection}
          unassignedAssets={unassignedAssets}
        />
      ),
    },
    {
      title: 'Bevestiging',
      content: <HardwareOnboardingComfirmation data={overviewData} />,
    },
  ];

  useEffect(() => {
    fetchAssetPacks();
  }, [userGroup]);

  useEffect(() => {
    fetchUserGroups();
    fetchUnassignedHardware();
  }, []);

  const items = steps.map((item) => ({ key: item.title, title: item.title }));

  return (
    <Form
      form={form}
      className={`mt-5 ${loading && 'opacity-50'}`}
      validateTrigger="onSubmit"
    >
      <Steps current={current} items={items} onChange={onChangeSteps} />

      <div className="py-12">{steps[current].content}</div>

      <div className="mt-5 flex gap-3 justify-start">
        {current > 0 && (
          <Cta
            text="Vorige"
            style="small"
            color="transparent"
            onClick={() => prev()}
          />
        )}

        {current < steps.length - 1 && (
          <Cta
            text="Volgende"
            style="small"
            color="default"
            onClick={() => next()}
          />
        )}

        {current === steps.length - 1 &&
          (loading ? (
            <LoadingSpinner />
          ) : (
            <Cta
              text="Indienen"
              style="small"
              color="default"
              onClick={handleSubmit}
            />
          ))}
      </div>
    </Form>
  );
};

export default IctOnboardingForm;
